// @flow

import React, { type Element, useCallback, useEffect, useState } from 'react'
import { Box, Container } from '@material-ui/core'
import StaticField from '../../AccountSettingsBC/StaticField'
import { grey } from '@material-ui/core/colors'
import FieldButtons from '../../AccountSettingsBC/FieldButtons'
import EmailField from './EmailField'
import { request } from '../../Common/Request'
import BasicPasswordField from '../BasicPasswordField'
import { BaseFormData } from '../../../lib/BaseFormData'
import { BaseValidationResult } from '../../../lib/BaseValidationResult'

type FormFields = 'email' | 'currentPassword'

class FormData extends BaseFormData<FormFields> {}
class ValidationResult extends BaseValidationResult<FormFields> {}

type Props = {|
  email: string,
  setEmail: (string) => void,
  handleServerErrors: ({ [string]: Array<string> }) => void,
  handleSuccess: (string) => void,
|}

function EmailContainer({
  email,
  setEmail,
  handleServerErrors,
  handleSuccess,
}: Props): Element<typeof React.Fragment> {
  const [errors, setErrors] = useState(new FormData())
  const [validationResult, setValidationResult] = useState(new ValidationResult())
  const [formData, setFormData] = useState(new FormData())
  const [isLoading, setIsLoading] = useState(false)
  const [updateDisabled, setUpdateDisabled] = useState(true)

  const [editMode, setEditMode] = useState(false)
  const enableEditMode = () => setEditMode(true)
  const disableEditMode = () => setEditMode(false)

  const fieldContent: string = email

  function handleCancel(): void {
    disableEditMode()
    handleFormDataChange('currentPassword', '')
    handleErrorsChange('currentPassword', '')
    setUpdateDisabled(true)
  }

  const handleErrorsChange = useCallback((fieldName: FormFields, value: string) => {
    setErrors((prevValue) => prevValue.update(fieldName, value))
  }, [])
  const handleFormDataChange = (fieldName: FormFields, value: string) => {
    setFormData((prevValue) => prevValue.update(fieldName, value))
  }
  const handleValidationResultChange = (fieldName: FormFields, isValid: boolean) => {
    setValidationResult((prevValue) => prevValue.update(fieldName, isValid))
  }

  const handleApiSuccess = () => {
    setIsLoading(false)
    setEmail(formData.value('email'))
    setFormData((prevValue) => prevValue.update('email', ''))
    setFormData((prevValue) => prevValue.update('currentPassword', ''))
    handleSuccess('Email')
    setEditMode(false)
  }

  function handleErrors(errors: { [string]: Array<string> }): void {
    setIsLoading(false)

    if ('email' in errors || 'current_password' in errors) {
      if ('email' in errors) {
        handleErrorsChange('email', errors.email[0])
        handleValidationResultChange('email', false)
      }
      if ('current_password' in errors) {
        handleErrorsChange('currentPassword', errors.current_password[0])
      }
    } else {
      handleServerErrors(errors)
    }
  }

  const setIsValid = useCallback(
    (isValid) => handleValidationResultChange('currentPassword', isValid),
    []
  )

  async function submitForm(): Promise<void> {
    setIsLoading(true)

    const path = '/api/v4/user_credentials'

    const payload = JSON.stringify({
      email: formData.value('email'),
      current_password: formData.value('currentPassword'),
    })

    await request(path, 'PATCH', {}, payload, handleErrors, handleApiSuccess)
  }

  useEffect(() => {
    if (formData.value('email') === email) {
      setUpdateDisabled(true)
    } else setUpdateDisabled(false)
  }, [formData, email])

  return (
    <Container maxWidth="sm" disableGutters>
      {editMode ? (
        <Box borderBottom={1} color={grey[300]} pb={2}>
          <Box pt={2}>
            <EmailField
              initialValue={email}
              value={formData.value('email')}
              setValue={(value) => handleFormDataChange('email', value)}
              setIsValid={(isValid) => handleValidationResultChange('email', isValid)}
              serverError={errors.value('email')}
              setServerError={handleErrorsChange}
            />
          </Box>
          <Box py={2}>
            <BasicPasswordField
              fieldName="Current Password"
              value={formData.value('currentPassword')}
              setValue={(value) => handleFormDataChange('currentPassword', value)}
              setIsValid={setIsValid}
              removeServerError={() => handleErrorsChange('currentPassword', '')}
              serverError={errors.value('currentPassword')}
            />
          </Box>
          <FieldButtons
            handleCancel={handleCancel}
            handleUpdate={submitForm}
            updateDisabled={updateDisabled || !validationResult.isValid() || isLoading}
            isLoading={isLoading}
          />
        </Box>
      ) : (
        <StaticField title="Email" content={fieldContent} handleEdit={enableEditMode} />
      )}
    </Container>
  )
}

export default EmailContainer
